import { useCallback } from 'react'

import useFetch from 'src/hooks/useFetch'
import useFreezedTagItemsStore from 'src/stores/useFreezedTagItemsStore'
import SaveDashboardItemInterface from 'src/types/SaveDashboardItemInterface'
import { getApiUrl } from 'src/utils'
import fixComponentSettingsFn from 'src/utils/componentSettingsUtils'
import { getDashboardKeyFn } from 'src/utils/saveDashboardUtils'
import { ComponentsSettings } from 'src/utils/settings'

export interface Dashboard {
  id: string
  label: string
  favorite: boolean
  selected: boolean
  sub_product?: string
  components_settings?: ComponentsSettings
}

const useDashboardsService = () => {
  const setFreezedTagItems = useFreezedTagItemsStore(s => s.setFreezedTagItems)

  const [, executeUpdateDashboard] = useFetch<SaveDashboardItemInterface[]>(
    {
      url: `${getApiUrl()}/api/admin/saved_dashboards/:id`,
      method: 'PUT',
    },
    { manual: true },
  )

  const [, executeSaveNewDashboard] = useFetch<SaveDashboardItemInterface[]>(
    {
      url: `${getApiUrl()}/api/admin/saved_dashboards`,
      method: 'POST',
    },
    { manual: true },
  )

  const [, executeRenameDashboard] = useFetch<SaveDashboardItemInterface[]>(
    { method: 'PUT' },
    { manual: true },
  )

  const [, executeGetDashboard] = useFetch<ComponentsSettings>({ method: 'GET' }, { manual: true })

  const [, executeSetFavoriteDashboard] = useFetch<SaveDashboardItemInterface[]>(
    {
      url: `${getApiUrl()}/api/admin/saved_dashboards/:id/favorite`,
      method: 'PUT',
    },
    { manual: true },
  )

  const [, executeDeleteDashboard] = useFetch<SaveDashboardItemInterface[]>(
    { method: 'DELETE' },
    { manual: true },
  )

  const getDashboard = useCallback(
    async ({
      id,
      programId,
      subProductCode,
    }: {
      id: string
      programId?: string
      subProductCode: string
    }) => {
      const data = await executeGetDashboard({
        url: `${getApiUrl()}/api/admin/saved_dashboards/${id}`,
        params: {
          program_id: programId,
        },
      })
      if (data?.data?.components_settings) {
        const scopes = data.data.components_settings[subProductCode]?.filterBar?.scopes || []

        const freezedTagItems = scopes.filter(scope => scope.freezed)?.map(scope => scope.id)

        const dashboardKey = getDashboardKeyFn({
          subProductCode,
          programId: programId?.toString(),
        })

        setFreezedTagItems(s => ({
          ...s,
          [dashboardKey]: freezedTagItems,
        }))

        return {
          ...data,
          data: {
            ...data.data,
            components_settings: fixComponentSettingsFn(
              data.data.components_settings as ComponentsSettings,
            ),
          },
        }
      }

      return data
    },
    [executeGetDashboard, setFreezedTagItems],
  )

  const updateDashboard = useCallback(
    async (id: string, settings: any) => {
      return executeUpdateDashboard({
        url: `${getApiUrl()}/api/admin/saved_dashboards/${id}`,
        data: settings,
      })
    },
    [executeUpdateDashboard],
  )

  const saveNewDashboard = useCallback(
    async (settings: any) => {
      return executeSaveNewDashboard({
        url: `${getApiUrl()}/api/admin/saved_dashboards`,
        data: settings,
      })
    },
    [executeSaveNewDashboard],
  )

  const deleteDashboard = useCallback(
    async ({ id, programId }: { id: string; programId?: string }) => {
      return executeDeleteDashboard({
        url: `${getApiUrl()}/api/admin/saved_dashboards/${id}`,
        params: {
          program_id: programId,
        },
      })
    },
    [executeDeleteDashboard],
  )

  const setAsFavoriteDashboard = useCallback(
    async ({
      id,
      code,
      program,
    }: {
      id: string | null
      code: string
      program: string | undefined
    }) => {
      return executeSetFavoriteDashboard({
        url: `${getApiUrl()}/api/admin/saved_dashboards/${id || 'default_dashboard'}/favorite`,
        data: { saved_dashboard: { sub_product: code, program_id: program } },
      })
    },
    [executeSetFavoriteDashboard],
  )

  const renameDashboard = useCallback(
    async ({ id, programId, label }: { id: string; programId?: string; label: string }) => {
      return executeRenameDashboard({
        url: `${getApiUrl()}/api/admin/saved_dashboards/${id}/rename`,
        data: { saved_dashboard: { label, program_id: programId } },
      })
    },
    [executeRenameDashboard],
  )

  return {
    updateDashboard,
    getDashboard,
    deleteDashboard,
    setAsFavoriteDashboard,
    renameDashboard,
    saveNewDashboard,
  }
}

export default useDashboardsService
